'use client';

import { create } from 'zustand';
import {
    computeCartTotals,
    EMPTY_CART_TOTALS,
    type CartTotalsInput,
    type CartTotalsSnapshot,
} from '@/entities/cart/lib/compute-cart-totals';

type PricingFns = Pick<
    CartTotalsSnapshot,
    'getItemPrices' | 'getItemFinalPrice' | 'getDeliveryMethodArea' | 'getDeliveryMethodAreaPrice'
>;

type CartStore = CartTotalsSnapshot & {
    /** Recompute snapshot from current form + query inputs. */
    recompute: (input: CartTotalsInput) => void;
};

/**
 * Latest pricing closures from computeCartTotals. Kept outside `set()` state so
 * `getItemPrices` / etc. identities on the store stay stable — otherwise every
 * tip/payment/time recompute replaces the fns and re-renders every product card
 * and the cart items list (they select those fns from the store).
 *
 * `getMinOrderRestriction` and `getPaymentMethodPrice` are intentionally NOT here:
 * both close over the cart's own sums, so their identity must change via `set()`
 * when totals recompute — otherwise delivery/payment method lists that select them
 * never re-render when the cart crosses a minimum-order threshold, or when a
 * percentage payment fee changes with the cart.
 *
 * Stability has a hard limit though: these closures are built FOR one currency,
 * country, language, menu snapshot and delivery method, and they answer in that
 * denomination forever. Holding the identity across a change to any of those is
 * what left the product detail page reading "120 Kč" after a switch to EUR
 * (ESH-PD-16) — nothing re-rendered, so nothing re-read the new closure. The
 * fns therefore go through `set()` whenever one of those inputs moves, and only
 * a cart-shaped recompute (tips, payment, time, quantities) keeps them stable.
 */
let pricingFns: PricingFns = {
    getItemPrices: EMPTY_CART_TOTALS.getItemPrices,
    getItemFinalPrice: EMPTY_CART_TOTALS.getItemFinalPrice,
    getDeliveryMethodArea: EMPTY_CART_TOTALS.getDeliveryMethodArea,
    getDeliveryMethodAreaPrice: EMPTY_CART_TOTALS.getDeliveryMethodAreaPrice,
};

/**
 * The inputs the pricing closures bake in. `menuLists` and `deliveryMethods` are
 * compared by reference on purpose — a new snapshot is a new price basis, and
 * the queries hand back the same object while it is unchanged.
 */
type PricingInputs = Pick<
    CartTotalsInput,
    'menuLists' | 'deliveryMethods' | 'selectedCurrency' | 'userLanguage'
> & {
    venueCountry: string | undefined;
    deliveryMethodId: string | null;
};

let pricingInputs: PricingInputs | null = null;

const pricingInputsOf = (input: CartTotalsInput): PricingInputs => ({
    menuLists: input.menuLists,
    deliveryMethods: input.deliveryMethods,
    selectedCurrency: input.selectedCurrency,
    userLanguage: input.userLanguage,
    venueCountry: input.venue?.address?.country,
    deliveryMethodId: input.form.deliveryMethod.id ?? null,
});

const samePricingInputs = (a: PricingInputs | null, b: PricingInputs): boolean =>
    !!a &&
    a.menuLists === b.menuLists &&
    a.deliveryMethods === b.deliveryMethods &&
    a.selectedCurrency === b.selectedCurrency &&
    a.userLanguage === b.userLanguage &&
    a.venueCountry === b.venueCountry &&
    a.deliveryMethodId === b.deliveryMethodId;

export const useCartStore = create<CartStore>((set) => ({
    ...EMPTY_CART_TOTALS,
    getItemPrices: ((...args) => pricingFns.getItemPrices(...args)) as CartTotalsSnapshot['getItemPrices'],
    getItemFinalPrice: ((...args) =>
        pricingFns.getItemFinalPrice(...args)) as CartTotalsSnapshot['getItemFinalPrice'],
    getDeliveryMethodArea: ((...args) =>
        pricingFns.getDeliveryMethodArea(...args)) as CartTotalsSnapshot['getDeliveryMethodArea'],
    getDeliveryMethodAreaPrice: ((...args) =>
        pricingFns.getDeliveryMethodAreaPrice(...args)) as CartTotalsSnapshot['getDeliveryMethodAreaPrice'],
    recompute: (input) => {
        const {
            getItemPrices,
            getItemFinalPrice,
            getPaymentMethodPrice,
            getDeliveryMethodArea,
            getDeliveryMethodAreaPrice,
            getMinOrderRestriction,
            ...scalars
        } = computeCartTotals(input);

        pricingFns = {
            getItemPrices,
            getItemFinalPrice,
            getDeliveryMethodArea,
            getDeliveryMethodAreaPrice,
        };

        const nextPricingInputs = pricingInputsOf(input);
        const pricingBasisChanged = !samePricingInputs(pricingInputs, nextPricingInputs);
        pricingInputs = nextPricingInputs;

        // Scalars + the two cart-dependent fns through set(); the rest stay stable
        // above unless the basis they price against moved, in which case their
        // identity has to move too so subscribers re-read them.
        set({
            ...scalars,
            getMinOrderRestriction,
            getPaymentMethodPrice,
            ...(pricingBasisChanged
                ? {
                      getItemPrices: ((...args) =>
                          pricingFns.getItemPrices(...args)) as CartTotalsSnapshot['getItemPrices'],
                      getItemFinalPrice: ((...args) =>
                          pricingFns.getItemFinalPrice(...args)) as CartTotalsSnapshot['getItemFinalPrice'],
                      getDeliveryMethodArea: ((...args) =>
                          pricingFns.getDeliveryMethodArea(
                              ...args,
                          )) as CartTotalsSnapshot['getDeliveryMethodArea'],
                      getDeliveryMethodAreaPrice: ((...args) =>
                          pricingFns.getDeliveryMethodAreaPrice(
                              ...args,
                          )) as CartTotalsSnapshot['getDeliveryMethodAreaPrice'],
                  }
                : {}),
        });
    },
}));
